import { apiFetch } from '@/lib/api-client';
import type { AttemptListResponse, BookOrderStatus, PreviewEnvelope } from './types';

/**
 * Book feature api layer.
 *
 * House pattern (`pwa/src/features/story/api.ts:1-6`): plain
 * path-to-Promise functions calling the shared `apiFetch`. No
 * react-query, no axios. `apiFetch` handles auth-header injection,
 * 401 refresh, timeout, and DreamableError envelope unwrapping
 * (see `pwa/src/lib/api-client.ts:93-161`).
 *
 * Endpoints:
 *   GET  /api/book/orders/:orderId              — preview-read.controller.ts:20-31
 *   POST /api/book/orders/:orderId/scenes/:sceneId/retry
 *                                                — scene-retry.controller.ts:22
 *
 * Global `api` prefix from `src/main.ts:75`.
 *
 * Sebastian ruling 2026-09-05 — `editBookTitle` + `EditBookTitleResponse`
 * removed with the deletion of `src/book/title-text/`. Vorschau title
 * is display-only; post-payment title changes fire through the cover-
 * regen endpoint (step 4, forthcoming) rather than an independent
 * title write.
 */

export function fetchBookOrder(orderId: string): Promise<PreviewEnvelope> {
  return apiFetch<PreviewEnvelope>(`/api/book/orders/${orderId}`);
}

/**
 * Cover-regen slice (Sebastian ruling 2026-09-05) — customer-facing
 * cover regeneration. Body carries an OPTIONAL title (max 40 chars)
 * + OPTIONAL comment (max 200 chars); at least one must be non-empty
 * after trim or the server returns `cover_regen_nothing_to_change`
 * (400). Over-cap → `cover_regen_cap_reached` (409). Wrong status
 * → `cover_regen_not_in_customer_review` (409). Over-40-char title
 * → `title_too_long` (400) — same code the deleted title-edit
 * endpoint used, retained across surfaces.
 */
export interface RegenCoverResponse {
  readonly status: 'regen_enqueued';
  readonly remainingCover: number;
}

export function regenCover(
  orderId: string,
  body: { title?: string | null; comment?: string | null },
): Promise<RegenCoverResponse> {
  // `apiFetch` calls `JSON.stringify(body)` unconditionally at
  // `pwa/src/lib/api-client.ts:110-112`. Pass the object; passing a
  // pre-stringified string would double-serialise.
  //
  // Jugendschutz launch-slice (Sebastian ruling 2026-09-10) — accept
  // `null` on either field so the failed-tile "Nochmal zeichnen" bare-
  // retry path can pass `{title: null, comment: null}` explicitly.
  // The server treats null / undefined / empty-after-trim identically
  // (`typeof x === 'string'` gate). The corresponding both-empty
  // refusal is RELAXED server-side when the latest cover AnchorAttempt
  // is in `error` status (see `CoverRegenService.regenCover`).
  //
  // Normalise nulls to undefined so JSON.stringify emits the fields
  // as absent rather than `null` — belt-and-braces since either form
  // works on the current server. Filter is minimal (no trim, no
  // shape mutation of non-null strings) to keep the wire behaviour
  // predictable.
  const normalisedBody: { title?: string; comment?: string } = {};
  if (typeof body.title === 'string') normalisedBody.title = body.title;
  if (typeof body.comment === 'string') normalisedBody.comment = body.comment;
  return apiFetch<RegenCoverResponse>(`/api/book/orders/${orderId}/cover/regen`, {
    method: 'POST',
    body: normalisedBody,
  });
}

/**
 * Response envelope for the scene retry endpoint. Mirrors the return
 * shape at `src/book/scene-retry/scene-retry.service.ts` (retry
 * returns void-ish; the wire always ships an ok envelope, we don't
 * consume the body).
 */
export interface RetrySceneResponse {
  readonly status: string;
}

export function retryScene(orderId: string, sceneId: string): Promise<RetrySceneResponse> {
  return apiFetch<RetrySceneResponse>(`/api/book/orders/${orderId}/scenes/${sceneId}/retry`, {
    method: 'POST',
  });
}

/**
 * Anchor accept and regen — screen 2 mutation surfaces.
 *
 * Endpoints:
 *   POST /api/book/orders/:orderId/anchor/accept — anchor.controller.ts:30-38
 *   POST /api/book/orders/:orderId/anchor/regen  — anchor.controller.ts:48-57
 *
 * Both bodies carry `anchorAttemptId` — the AnchorAttempt.id read from
 * `envelope.cover.attemptId`. Server re-verifies staleness via the
 * `anchor_attempt_stale` error code; the client does not race the
 * envelope, it reads the current attemptId at click time and lets the
 * server say yes or no.
 */

export interface AnchorAcceptResponse {
  readonly status: 'accepted' | 'already_accepted';
  readonly bookOrderStatus: BookOrderStatus;
}

export function acceptAnchor(
  orderId: string,
  anchorAttemptId: string,
): Promise<AnchorAcceptResponse> {
  // `apiFetch` calls `JSON.stringify(body)` unconditionally at
  // `pwa/src/lib/api-client.ts:110-112`. Pass the object; passing a
  // pre-stringified string would double-serialise.
  return apiFetch<AnchorAcceptResponse>(`/api/book/orders/${orderId}/anchor/accept`, {
    method: 'POST',
    body: { anchorAttemptId },
  });
}

export interface AnchorRegenResponse {
  readonly status: string;
}

export function regenAnchor(
  orderId: string,
  anchorAttemptId: string,
): Promise<AnchorRegenResponse> {
  // `apiFetch` calls `JSON.stringify(body)` unconditionally at
  // `pwa/src/lib/api-client.ts:110-112`. Pass the object; passing a
  // pre-stringified string would double-serialise.
  return apiFetch<AnchorRegenResponse>(`/api/book/orders/${orderId}/anchor/regen`, {
    method: 'POST',
    body: { anchorAttemptId },
  });
}

/**
 * Scene-text edit and leave-editing — screen 3 mutation surfaces.
 *
 * Endpoints (both existed since Chat 41 per Sebastian's brief):
 *   POST /api/book/orders/:orderId/scenes/:sceneId/text  — scene-text.controller.ts:28-38
 *   POST /api/book/orders/:orderId/text-editing/leave    — scene-text.controller.ts:50-58
 *
 * Corridor enforcement is server-side (G7). The PWA reads the
 * bounds through `envelope.textCorridor` (`types.ts`) since sister
 * session's `8bc4418`.
 */

export interface EditSceneTextResponse {
  readonly status: 'saved';
  readonly bookOrderStatus: BookOrderStatus;
  /**
   * Block 2 c5 (2026-09-01) — whether the sync-compose tail ran.
   * `false` when the scene had no usable RenderAttempt matching the
   * current assembled_prompt.bodyHash (typically the 16 non-preview
   * scenes pre-payment). Server persists the text either way; the
   * customer's next envelope fetch shows `composedImageUrl=null` for
   * scenes that skipped compose, and the PWA renders those as
   * "rendering" placeholders. Not surfaced in the UI today —
   * informational for callers that care.
   */
  readonly composed: boolean;
}

export function editSceneText(
  orderId: string,
  sceneId: string,
  text: string,
): Promise<EditSceneTextResponse> {
  // `apiFetch` calls `JSON.stringify(body)` unconditionally at
  // `pwa/src/lib/api-client.ts:110-112`. Pass the object; passing a
  // pre-stringified string would double-serialise.
  return apiFetch<EditSceneTextResponse>(`/api/book/orders/${orderId}/scenes/${sceneId}/text`, {
    method: 'POST',
    body: { text },
  });
}

/**
 * Position-1 Commit-3 (Sebastian ruling 2026-09-03) — customer
 * move-panel PATCH response. `status: 'already_saved'` when the
 * incoming coords match what is already on the picked RenderAttempt;
 * the server does not re-fire the sync-compose in that case. Client
 * treats both statuses as success. `panelBox` returns the persisted
 * server-side truth so the client reconciles from it rather than
 * from its own optimistic state.
 */
export interface MovePanelResponse {
  readonly status: 'saved' | 'already_saved';
  readonly panelBox: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
    readonly source: 'customer';
  };
}

export function movePanel(
  orderId: string,
  sceneId: string,
  left: number,
  top: number,
): Promise<MovePanelResponse> {
  return apiFetch<MovePanelResponse>(
    `/api/book/orders/${orderId}/scenes/${sceneId}/panel-position`,
    {
      method: 'PATCH',
      body: { left, top },
    },
  );
}

/**
 * Resizable-panel slice (Sebastian ruling 2026-09-17) — customer
 * resize-panel PATCH response. Server clamps + auto-widens the
 * requested width; the returned `panel` reflects the effective drawn
 * geometry AFTER any auto-widen + shift-back. Shape mirrors
 * `PreviewPage.drawnPanel` so the client renders the returned rect
 * verbatim. On `scene_text_panel_overflow` the endpoint throws an
 * `ApiError` (the customer sees the German error message via the
 * envelope's `user_message`).
 */
export interface ResizePanelResponse {
  readonly status: 'saved' | 'already_saved';
  readonly panel: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
    readonly writableWidth: number;
  };
}

export function resizePanel(
  orderId: string,
  sceneId: string,
  width: number,
): Promise<ResizePanelResponse> {
  return apiFetch<ResizePanelResponse>(
    `/api/book/orders/${orderId}/scenes/${sceneId}/panel-width`,
    {
      method: 'PATCH',
      body: { width },
    },
  );
}

/**
 * Sebastian ruling 2026-09-17 fix-5 — combined text + panel edit
 * (item D). One transaction, one response.
 */
export interface PanelAndTextRequest {
  readonly text: string;
  readonly width: number;
  readonly left: number;
  readonly top: number;
}

export interface PanelAndTextResponse {
  readonly status: 'saved' | 'already_saved';
  readonly panel: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
    readonly writableWidth: number;
  };
  readonly bookOrderStatus?: BookOrderStatus;
}

export interface PanelAndTextPreviewResponse {
  readonly panel: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
    readonly writableWidth: number;
  };
}

/**
 * Live-preview call. Read-only server-side. The PWA fires this from
 * the combined editor after a short typing pause (~400 ms) so the
 * panel outline follows the customer's edits without a save.
 */
export function previewPanelAndText(
  orderId: string,
  sceneId: string,
  body: PanelAndTextRequest,
): Promise<PanelAndTextPreviewResponse> {
  return apiFetch<PanelAndTextPreviewResponse>(
    `/api/book/orders/${orderId}/scenes/${sceneId}/panel-and-text/preview`,
    {
      method: 'POST',
      body,
    },
  );
}

/**
 * Combined save. Writes text + width + left + top in one server-side
 * transaction. The response's `panel` reflects the effective drawn
 * geometry AFTER auto-widen + frame clamp.
 */
export function savePanelAndText(
  orderId: string,
  sceneId: string,
  body: PanelAndTextRequest,
): Promise<PanelAndTextResponse> {
  return apiFetch<PanelAndTextResponse>(
    `/api/book/orders/${orderId}/scenes/${sceneId}/panel-and-text`,
    {
      method: 'PATCH',
      body,
    },
  );
}

export interface LeaveTextEditingResponse {
  readonly status: 'left' | 'already_left';
  readonly bookOrderStatus: BookOrderStatus;
}

export function leaveTextEditing(orderId: string): Promise<LeaveTextEditingResponse> {
  return apiFetch<LeaveTextEditingResponse>(`/api/book/orders/${orderId}/text-editing/leave`, {
    method: 'POST',
  });
}

/**
 * Position-1 Commit-4 (Sebastian ruling 2026-09-04) — customer re-
 * render endpoint. Option 3 of the Freigabe change dialog.
 *
 * Body: `{ comment?: string }`. Non-empty comment is injected verbatim
 * into the image prompt at render time (step-12a hint block). Empty /
 * omitted comment enqueues a bare regen (still consumes one attempt
 * per the customer's explicit request for a new image). Moderation on
 * this surface was removed 2026-09-04 — content-safety belongs to
 * Jugendschutz (tracker `[jugendschutz-scene-regen-comment]`).
 *
 * On success the response carries the remaining per-page + per-book
 * allowance so the ReviewScreen can display them after the customer
 * has used at least one regen (T3 staffelung — display rules stay
 * client-side).
 *
 * Error codes the endpoint may return (envelope):
 *   scene_regen_page_cap_reached    (409)  — 3/3 used on this page; cta=mailto:support
 *   scene_regen_book_cap_reached    (409)  — 10/10 used on this book; cta=mailto:support
 *   scene_regen_not_in_customer_review (409) — wrong status
 *   scene_not_found                 (404)
 *   book_order_not_found            (404, no info leak)
 */
export interface RegenSceneResponse {
  readonly status: 'regen_enqueued';
  readonly sceneId: string;
  readonly attemptNo: number;
  readonly remainingPage: number;
  readonly remainingBook: number;
}

export function regenScene(
  orderId: string,
  sceneId: string,
  comment: string | null,
): Promise<RegenSceneResponse> {
  const body = comment === null || comment.trim().length === 0 ? {} : { comment: comment.trim() };
  return apiFetch<RegenSceneResponse>(`/api/book/orders/${orderId}/scenes/${sceneId}/regen`, {
    method: 'POST',
    body,
  });
}

/**
 * Position-5 Commit 6b.3 (Sebastian ruling 2026-09-02) — three summary-
 * review endpoints. Retry and self-write became queued jobs — the
 * endpoint accepts, enqueues, returns 202 { enqueued: true }
 * immediately. The screen polls `envelope.summaryReview.retryInFlight`
 * and `selfWriteInFlight` for the run outcome. Release stays
 * synchronous (sub-second, 3 DB ops + 1 enqueue) and continues to
 * return `{ bookOrderStatus }`.
 *
 * Endpoints:
 *   POST /api/book/orders/:orderId/summary-review/retry         → 202
 *   POST /api/book/orders/:orderId/summary-review/release       → 200
 *   POST /api/book/orders/:orderId/summary-review/self-write    → 202
 *
 * Error codes the controller may return (mapped by DreamableError
 * envelope on non-2xx):
 *   summary_review_wrong_status                (409)
 *   summary_review_cap_exhausted               (409, retry past cap)
 *   summary_review_self_write_not_allowed      (409, self-write pre-cap)
 *   summary_review_action_in_flight            (409, retry/self-write already in flight)
 *   summary_review_generation_failed           (502, pre-model failed — worker side)
 *   summary_review_missing_required_field      (422, entry check failed)
 *   book_order_not_found                       (404)
 */

export interface SummaryReviewEnqueueResponse {
  readonly enqueued: true;
}

export interface SummaryReviewReleaseResponse {
  readonly bookOrderStatus: BookOrderStatus;
}

export function retrySummaryReview(
  orderId: string,
  customerChange: string,
  // Optional titelwunsch override (Sebastian ruling 2026-09-10 late
  // evening). Undefined → payload omits the field (backwards-compatible
  // for callers that only carry customerChange). Defined string is
  // sent verbatim; backend normalises (trim, empty → null) and runs
  // BlocklistService on non-empty new titles.
  titelwunsch?: string,
): Promise<SummaryReviewEnqueueResponse> {
  return apiFetch<SummaryReviewEnqueueResponse>(
    `/api/book/orders/${orderId}/summary-review/retry`,
    {
      method: 'POST',
      body: titelwunsch === undefined ? { customerChange } : { customerChange, titelwunsch },
    },
  );
}

export function releaseSummaryReview(orderId: string): Promise<SummaryReviewReleaseResponse> {
  return apiFetch<SummaryReviewReleaseResponse>(
    `/api/book/orders/${orderId}/summary-review/release`,
    {
      method: 'POST',
    },
  );
}

export function selfWriteSummaryReview(
  orderId: string,
  text: string,
): Promise<SummaryReviewEnqueueResponse> {
  return apiFetch<SummaryReviewEnqueueResponse>(
    `/api/book/orders/${orderId}/summary-review/self-write`,
    {
      method: 'POST',
      body: { text },
    },
  );
}

/**
 * Cast-photo swap — screen `photo_rejected` mutation surface.
 *
 * Endpoint:
 *   POST /api/book/orders/:orderId/cast/:castKey/photo
 *
 * Body shape mirrors backend `SwapCastPhotoDto` at
 * `src/book/photo-swap/photo-swap.dto.ts` verbatim:
 *   { whichPhoto: 'face' | 'fullBody', newPhotoKey: string }
 *
 * Sebastian ruling 2026-08-12 (Ruling 1 of the afternoon wave): the
 * PWA adopts the backend shape. Pre-fix the PWA was sending the
 * legacy `{facePhotoKey}` / `{fullBodyPhotoKey}` alternation (the
 * fourth contract mismatch on the same screen in one day). The DTO
 * rewrite this morning made `{whichPhoto, newPhotoKey}` the explicit
 * contract; the PWA caller had not caught up.
 *
 * The PWA obtains `newPhotoKey` by calling
 * `usePhotoUpload().upload([file])` first (same signed-uploads
 * endpoint as the Light-Gate bulk path); the returned key goes on
 * the body under `newPhotoKey`. `whichPhoto` is threaded from the
 * envelope's `intake.derived.rejection.whichPhoto`.
 *
 * On success the server accepts the new photo, re-runs safety, and
 * transitions the order back into its normal pre-payment status. The
 * PWA refetches the envelope; BookRoute re-dispatches on the new
 * status.
 */

export interface SwapCastPhotoRequest {
  readonly whichPhoto: 'face' | 'fullBody';
  readonly newPhotoKey: string;
}

export interface SwapCastPhotoResponse {
  readonly status: 'swapped';
  readonly bookOrderStatus: BookOrderStatus;
}

export function swapCastPhoto(
  orderId: string,
  castKey: string,
  body: SwapCastPhotoRequest,
): Promise<SwapCastPhotoResponse> {
  return apiFetch<SwapCastPhotoResponse>(`/api/book/orders/${orderId}/cast/${castKey}/photo`, {
    method: 'POST',
    body,
  });
}

/**
 * Sebastian ruling 2026-09-24 (batch photo-swap slice) — stage a
 * voluntary photo swap on the anchor-review screen without firing a
 * render.
 *
 *   POST /api/book/orders/:orderId/cast/:castKey/photo/stage
 *
 * The backend sanitises the new photo bytes in place, writes the
 * entry to `intake.derived.pendingPhotoSwaps`, and does NOT flip
 * status. Multiple stages accumulate until the customer taps
 * "Neue Figuren erstellen" (see `redrawFigures` below).
 */
export interface StagePhotoSwapRequest {
  readonly whichPhoto: 'face' | 'fullBody';
  readonly newPhotoKey: string;
}

export interface StagePhotoSwapResponse {
  readonly orderId: string;
}

export function stagePhotoSwap(
  orderId: string,
  castKey: string,
  body: StagePhotoSwapRequest,
): Promise<StagePhotoSwapResponse> {
  return apiFetch<StagePhotoSwapResponse>(
    `/api/book/orders/${orderId}/cast/${castKey}/photo/stage`,
    { method: 'POST', body },
  );
}

/**
 * Sebastian ruling 2026-09-24 (batch photo-swap slice) — discard a
 * pending photo swap ("Altes Foto behalten").
 *
 *   DELETE /api/book/orders/:orderId/cast/:castKey/photo/pending/:whichPhoto
 *
 * Silent no-op when there is no pending entry for (castKey,
 * whichPhoto). Best-effort deletes the staged storage object.
 */
export function discardPendingPhotoSwap(
  orderId: string,
  castKey: string,
  whichPhoto: 'face' | 'fullBody',
): Promise<{ orderId: string }> {
  return apiFetch<{ orderId: string }>(
    `/api/book/orders/${orderId}/cast/${castKey}/photo/pending/${whichPhoto}`,
    { method: 'DELETE' },
  );
}

/**
 * Sebastian ruling 2026-09-24 (batch photo-swap slice) — "Neue Figuren
 * erstellen". Applies all pending photo swaps in one batch, runs
 * block derivation for every changed figure in parallel, and fires
 * ONE anchor render. Burns ONE attempt from the shared cap regardless
 * of pending count.
 *
 *   POST /api/book/orders/:orderId/anchor/redraw
 */
export function redrawFigures(orderId: string): Promise<{ orderId: string }> {
  return apiFetch<{ orderId: string }>(`/api/book/orders/${orderId}/anchor/redraw`, {
    method: 'POST',
  });
}

/**
 * Sebastian correction slice 2026-09-10 (Part 2.1) — customer-facing
 * book_anchor retry endpoint. Fires when the customer sees the
 * "Nochmal zeichnen" button on the ImageRefusedScreen (technical
 * branch, `envelope.intake.derived.imageRefusal.cause === 'technical'`).
 *
 * Endpoint:
 *   POST /api/book/orders/:orderId/anchor-retry
 *
 * Body: none — precondition (status === image_refused, stage ===
 * book_anchor, cause === technical) is server-verified from
 * persisted state. Response: `{ orderId, status: 'anchor_retry_enqueued' }`.
 *
 * On success the backend clears `intake.derived.imageRefusal`,
 * transitions `image_refused → anchor_pending`, and enqueues a fresh
 * anchor render via `AnchorCoverAuthoringService.run`. The PWA
 * refetches; `BookRoute` re-dispatches onto the anchor_pending wait
 * screen.
 */
export interface AnchorRetryResponse {
  readonly orderId: string;
  readonly status: 'anchor_retry_enqueued';
}

export function retryBookAnchor(orderId: string): Promise<AnchorRetryResponse> {
  return apiFetch<AnchorRetryResponse>(`/api/book/orders/${orderId}/anchor-retry`, {
    method: 'POST',
  });
}

/**
 * Cast-photo delete — screen `photo_rejected` "drop this optional
 * photo instead of replacing it" mutation surface. Sebastian ruling
 * 2026-08-12 (wave commit 2): a customer whose optional Ganzkörper
 * photo was rejected AND who does not have a replacement handy
 * needs a way to drop it without abandoning the book.
 *
 * Endpoint:
 *   DELETE /api/book/orders/:orderId/cast/:castKey/photo/:whichPhoto
 *
 * `whichPhoto` is FIXED to 'fullBody'. Face is mandatory per §A3.2
 * E-19 — the backend rejects any other value at the controller with
 * `book_photo_delete_face_forbidden`. The type narrows here to
 * enforce the same at the caller. The PWA "Foto entfernen" button
 * only renders when `envelope.intake.derived.rejection.whichPhoto`
 * is `'fullBody'`.
 *
 * On success the server NULLs `intake.cast[i].fullBodyPhotoKey`,
 * CLEARS `intake.derived.rejection`, and transitions the order
 * back into its normal pre-payment status. Same success shape as
 * `swapCastPhoto` — PWA refetches; BookRoute re-dispatches on the
 * new status.
 */
export interface DeleteCastPhotoResponse {
  readonly orderId: string;
}

export function deleteCastPhoto(
  orderId: string,
  castKey: string,
  whichPhoto: 'fullBody',
): Promise<DeleteCastPhotoResponse> {
  return apiFetch<DeleteCastPhotoResponse>(
    `/api/book/orders/${orderId}/cast/${castKey}/photo/${whichPhoto}`,
    { method: 'DELETE' },
  );
}

/**
 * 2026-10-14 — remove ONE cast entry entirely from a book stuck at
 * `block_derivation_rejected`. Called from the Entfernen button on
 * BlockDerivationRejectedScreen. Backend contract at
 * `src/book/photo-swap/photo-swap.controller.ts` DELETE
 * `/api/book/orders/:orderId/cast/:castKey`. Server-side preconditions
 * (status, castKey ∈ rejections, minimum-cast-of-one) throw a
 * DreamableError; the PWA refetches on success and relies on the
 * client-side `cast.length === 1` guard to hide the button when
 * removal would be refused.
 */
export interface RemoveCastEntryResponse {
  readonly orderId: string;
}

export function removeCastEntry(
  orderId: string,
  castKey: string,
): Promise<RemoveCastEntryResponse> {
  return apiFetch<RemoveCastEntryResponse>(`/api/book/orders/${orderId}/cast/${castKey}`, {
    method: 'DELETE',
  });
}

// ── 6-3 book checkout (Sebastian ruling 2026-08-15) ────────────────

/**
 * POST /api/book/orders/:orderId/checkout — response envelope.
 *
 * Sebastian ruling 2026-09-24 (P14 pay-with-balance block 5): the
 * response is now DISCRIMINATED by `paidWithBalance`. Two shapes:
 *   - `paidWithBalance: false` → checkoutUrl is a Stripe hosted-page
 *     URL; PWA does `window.location.href = checkoutUrl` and leaves
 *     the SPA.
 *   - `paidWithBalance: true`  → checkoutUrl is null (no Stripe hop);
 *     PWA navigates to `/book/:orderId/erfolg` (NO `?session_id=…`
 *     query — BestellungErfolgRoute detects the missing query and
 *     skips the checkout-status poll, reading state from
 *     `/book/:orderId` directly).
 */
export interface CreateBookCheckoutResponse {
  readonly checkoutUrl: string | null;
  readonly paidWithBalance: boolean;
  readonly balanceAppliedCents: number;
  readonly amountChargedCents: number;
}

/**
 * Optional shipping-address block. REQUIRED when the customer's
 * balance covers the full order total (fully-covered branch — Stripe
 * never runs, so the PWA collects the address). OMITTED on the
 * Stripe paths (zero balance + partial cover) — Stripe collects the
 * address via `shipping_address_collection`.
 *
 * DE + AT (Sebastian ruling 2026-11-02 — Austria launch). The PWA's
 * country toggle writes `'DE'` or `'AT'` into this field; adding
 * more countries later means widening the DTO enum on the backend
 * AND the Stripe `allowed_countries` on the same commit.
 */
export interface BookCheckoutShippingAddress {
  readonly recipientName: string;
  readonly line1: string;
  readonly line2: string | null;
  readonly postalCode: string;
  readonly city: string;
  readonly country: 'DE' | 'AT';
}

/**
 * Third arg `acknowledgmentAccepted` — §312g Abs. 2 Nr. 1 BGB
 * personalization-acknowledgment (Sebastian ruling 2026-09-09
 * Bestellübersicht slice). Only BestellungReviewScreen may pass
 * `true`, and only after the customer has ticked the checkbox.
 *
 * Fourth arg `shippingAddress` — REQUIRED on the fully-covered
 * balance-only path (Sebastian Q1 ruling 2026-09-24). BestellungReviewScreen
 * checks the balance-preview result and includes the address block
 * when `paidWithBalance` would be true. On any Stripe path the arg
 * is omitted; passing it is safe (backend service ignores it on the
 * Stripe branches).
 */
/**
 * GA4 attribution snapshot (Sebastian ruling 2026-10-21). Threaded
 * into the Stripe session metadata on the Stripe path and written
 * directly to the BookOrder row on the balance-only path, so the
 * server-side Measurement Protocol listener can attribute `purchase`
 * / `book_redeemed` to the original Google Ads session.
 *
 * All fields optional on the wire. Server stores whatever arrives
 * and the MP listener gates on `consentAnalytics === true` AND
 * `clientId !== null`.
 */
export interface BookCheckoutGa4Snapshot {
  readonly clientId?: string;
  readonly sessionId?: string;
  readonly consentAnalytics?: boolean;
  readonly consentAds?: boolean;
}

/**
 * Meta Conversions API attribution snapshot forwarded on the book
 * checkout call (2026-10-29). All fields optional; the backend
 * persists fbc/fbp + request IP/UA only when `consentMarketing` is
 * true (PII minimisation). Deserialises on the server to
 * MetaAttributionDto.
 */
export interface BookCheckoutMetaSnapshot {
  readonly consentMarketing?: boolean;
  readonly fbc?: string;
  readonly fbp?: string;
}

export function createBookCheckout(
  orderId: string,
  salesUnit: string,
  acknowledgmentAccepted: true,
  shippingAddress?: BookCheckoutShippingAddress,
  ga4?: BookCheckoutGa4Snapshot,
  meta?: BookCheckoutMetaSnapshot,
  shippingCountry?: 'DE' | 'AT',
): Promise<CreateBookCheckoutResponse> {
  const body: Record<string, unknown> = { salesUnit, acknowledgmentAccepted };
  if (shippingAddress) body.shippingAddress = shippingAddress;
  if (ga4) body.ga4 = ga4;
  if (meta) body.meta = meta;
  // Sebastian ruling 2026-11-02 — the chosen shipping country goes to
  // the backend on the Stripe path so `allowed_countries` + shipping
  // amount resolve to the right value. Backend omits it = 'DE'
  // fallback (backward-compatible with older PWA builds).
  if (shippingCountry) body.shippingCountry = shippingCountry;
  return apiFetch<CreateBookCheckoutResponse>(`/api/book/orders/${orderId}/checkout`, {
    method: 'POST',
    body,
  });
}

// ── P14 pay-with-balance preview (Sebastian ruling 2026-09-24 Q2) ──

/**
 * GET /api/book/orders/:orderId/balance-preview — read-only preview
 * of what the customer's balance would cover for this order at this
 * instant. Non-authoritative: a fresh preview MUST be taken on every
 * POST /checkout because balance may have moved (another tab spent,
 * admin adjusted, etc.).
 */
export interface BookBalancePreviewResponse {
  readonly totalCents: number;
  readonly balanceAppliedCents: number;
  readonly amountChargedCents: number;
  readonly paidWithBalance: boolean;
  readonly debits: ReadonlyArray<{
    readonly giftCodeId: string;
    readonly expiresAt: string;
    readonly amountCents: number;
  }>;
}

export function fetchBookBalancePreview(
  orderId: string,
  salesUnit?: string,
  country?: 'DE' | 'AT',
): Promise<BookBalancePreviewResponse> {
  // Sebastian ruling 2026-09-25 (E2E-test case C finding 2) — pass
  // the currently-selected slug so the backend can compute the
  // preview BEFORE `BookOrder.salesUnit` is written (that field
  // only lands at paid-flip). Without the slug the backend throws
  // `book_order_not_checkoutable` and Guthaben / Zu zahlen rows
  // disappear from the Bestellübersicht.
  //
  // Sebastian ruling 2026-11-02 — pass the chosen country so the
  // backend's preview reflects AT shipping when selected. Omitted =
  // DE fallback on the backend (backward-compatible for older
  // builds). URLSearchParams handles encoding + the optional pair.
  const params = new URLSearchParams();
  if (salesUnit) params.set('salesUnit', salesUnit);
  if (country) params.set('country', country);
  const query = params.toString();
  const suffix = query.length > 0 ? `?${query}` : '';
  return apiFetch<BookBalancePreviewResponse>(
    `/api/book/orders/${orderId}/balance-preview${suffix}`,
  );
}

/**
 * GET /api/book/orders/:orderId/checkout-status — response envelope.
 * Mirrors backend `BookCheckoutStatusResponseDto`. Used by
 * `BestellungErfolgRoute` to poll after a Stripe redirect: waits
 * for `status === 'completed'` (webhook fired + DB flip landed),
 * then navigates to `/book/:orderId` where `BookRoute` re-dispatches
 * onto the Leiste-B "Bestellung erledigt · Freigabe offen" screen.
 */
export interface BookCheckoutStatusResponse {
  readonly status: 'pending' | 'completed' | 'expired';
  readonly bookOrderId: string;
  readonly salesUnit: string;
  readonly priceCents: number;
  /**
   * GA4 purchase projection (Google Ads conversion tracking,
   * 2026-10-13). `transactionId` is the BookOrder id — stable across
   * webhook retries and reloads, used as the GA4 `transaction_id`
   * for Google Ads deduplication. `amountTotalCents` /
   * `shippingCents` / `currency` are null when `status !== 'completed'`;
   * `amountTotalCents` is `0` for 100% promo codes and for balance-
   * only paid orders (BestellungErfolgRoute skips the GA4 event when
   * value <= 0).
   */
  readonly transactionId: string;
  readonly amountTotalCents: number | null;
  readonly shippingCents: number | null;
  readonly currency: string | null;
}

export function getBookCheckoutStatus(orderId: string): Promise<BookCheckoutStatusResponse> {
  return apiFetch<BookCheckoutStatusResponse>(`/api/book/orders/${orderId}/checkout-status`);
}

/**
 * POST /api/book/orders/:orderId/checkout-cancelled — customer-return
 * reset (Sebastian ruling 2026-09-08 evening). Called by BestellungRoute
 * on `?checkout=cancelled` arrival (Stripe cancel_url redirect).
 *
 * Backend rolls checkout_pending → preview_ready via race-safe
 * conditional updateMany. Any other status is a quiet no-op — a paid
 * order stays paid, a customer reloading the returned page hits the
 * same no-op idempotently.
 *
 * `reset` is true when this call flipped the row; false when the WHERE
 * clause matched nothing (paid, already reset, wrong status). PWA
 * ignores the boolean today — the mount-effect just refetches the
 * envelope so the screen re-renders with the fresh status.
 */
export function resetBookCheckoutCancelled(orderId: string): Promise<{ ok: true; reset: boolean }> {
  return apiFetch<{ ok: true; reset: boolean }>(`/api/book/orders/${orderId}/checkout-cancelled`, {
    method: 'POST',
  });
}

/**
 * 8a Freigabe — customer print-approval mutation (Sebastian ruling
 * 2026-08-17).
 *
 * Endpoint:
 *   POST /api/book/orders/:orderId/release — release.controller.ts
 *
 * No body. The auth guard identifies the user, the URL identifies
 * the order, and the server verifies ownership + status precondition
 * + races on the conditional update. On success it enqueues the
 * print job via `BookPipelineService.startPrint`.
 *
 * Response `status`:
 *   - 'released'         — this call flipped customer_review → print_approved.
 *   - 'already_released' — sequential or concurrent double-release;
 *                          idempotent, the order is at print_approved.
 *
 * The PWA refetches the envelope on either result; `BookRoute`
 * re-dispatches on the new status.
 */
export interface BookReleaseResponse {
  readonly status: 'released' | 'already_released';
  readonly bookOrderStatus: BookOrderStatus;
}

export function releaseBookOrder(orderId: string): Promise<BookReleaseResponse> {
  return apiFetch<BookReleaseResponse>(`/api/book/orders/${orderId}/release`, {
    method: 'POST',
  });
}

/**
 * Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) — four
 * fetch helpers pair up as symmetric slot handlers (scene + cover),
 * one component per surface pattern.
 *
 * List endpoints mint signed URLs per call (`attempt.imageUrl` is
 * fresh, not from envelope). Ordered by attemptNo asc so the customer
 * sees his sequence in the order he made it. `activeAttemptId` is the
 * customer's current pick when set, else newest — same rule as the
 * print picker.
 *
 * Pick endpoints POST just `{ attemptId }`. 404 on wrong id / stale
 * envelope (recovery: refetch preview). 409 on wrong book status.
 */

export function listSceneAttempts(orderId: string, sceneId: string): Promise<AttemptListResponse> {
  return apiFetch<AttemptListResponse>(`/api/book/orders/${orderId}/scenes/${sceneId}/attempts`);
}

export function pickSceneAttempt(
  orderId: string,
  sceneId: string,
  attemptId: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/book/orders/${orderId}/scenes/${sceneId}/attempts/pick`, {
    method: 'POST',
    body: { attemptId },
  });
}

export function listCoverAttempts(orderId: string): Promise<AttemptListResponse> {
  return apiFetch<AttemptListResponse>(`/api/book/orders/${orderId}/cover/attempts`);
}

export function pickCoverAttempt(orderId: string, attemptId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/book/orders/${orderId}/cover/attempts/pick`, {
    method: 'POST',
    body: { attemptId },
  });
}

/**
 * Anchor picker SPLIT (Sebastian ruling 2026-09-08 evening) —
 * customer picks between his anchor attempts at anchor_review; the
 * pick is a SELECTION, not an accept. The Accept button remains its
 * own step on the anchor review screen.
 *
 * Sebastian's ruling verbatim: "right about the backend mechanics
 * and wrong about the screen. Separate them: picking sets the
 * selection and re-renders the screen. Accepting is still its own
 * button." Yesterday's `pickBookAnchorAttempt` (which returned an
 * `AnchorAcceptResponse`) is superseded by `selectBookAnchorAttempt`
 * below; the endpoint URL is unchanged for stability.
 *
 * Endpoints:
 *   GET  /api/book/orders/:orderId/anchor/attempts — anchor.controller.ts
 *   POST /api/book/orders/:orderId/anchor/pick     — anchor.controller.ts
 *
 * On pick, the server writes `Anchor.selectedAnchorAttemptId` and
 * NOTHING else — no status flip, no story enqueue. Client refetches
 * the envelope so `envelope.cover.imageUrl` re-derives from the
 * selection. Accept still flows through `acceptAnchor` above and
 * carries the current envelope's attemptId; the accept's server-
 * side staleness is now selection-aware and honors the pick.
 */

export function listBookAnchorAttempts(orderId: string): Promise<AttemptListResponse> {
  return apiFetch<AttemptListResponse>(`/api/book/orders/${orderId}/anchor/attempts`);
}

export function selectBookAnchorAttempt(
  orderId: string,
  anchorAttemptId: string,
): Promise<{ ok: true; selectedAnchorAttemptId: string }> {
  return apiFetch<{ ok: true; selectedAnchorAttemptId: string }>(
    `/api/book/orders/${orderId}/anchor/pick`,
    {
      method: 'POST',
      body: { anchorAttemptId },
    },
  );
}

/**
 * Meine Bücher list + discard endpoints (Sebastian ruling 2026-09-15).
 *
 * `listBookOrders` — GET /api/book/orders. Returns the caller's book
 * orders (open + past), sorted updatedAt desc. Powers the /book/meine
 * screen.
 *
 * `discardBookOrder` — DELETE /api/book/orders/:orderId. Discards a
 * pre-payment order; server rejects with `book_order_not_discardable`
 * (409) if the order does not belong to the caller, does not exist,
 * or is post-payment. On success the Prisma cascade wipes scenes /
 * anchors / attempts / api-cost rows; uploaded photos are best-effort
 * deleted server-side.
 */
export interface CustomerCollectionOrder {
  readonly bookOrderId: string;
  readonly status: BookOrderStatus;
  readonly title: string;
  /**
   * Cast names entered by the customer at Personen (empty entries
   * filtered server-side). Carries the card's secondary line so two
   * early drafts stay distinguishable before a title exists.
   */
  readonly castNames: readonly string[];
  readonly updatedAt: string;
  readonly createdAt: string;
  readonly salesUnit: string | null;
}

export interface ListBookOrdersResponse {
  readonly orders: readonly CustomerCollectionOrder[];
}

export function listBookOrders(): Promise<ListBookOrdersResponse> {
  return apiFetch<ListBookOrdersResponse>('/api/book/orders');
}

export function discardBookOrder(orderId: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/book/orders/${orderId}`, { method: 'DELETE' });
}
